import { useEffect, useRef } from 'react';
import api from '../../lib/api';
import { syncSellerPush, isPushDeliveryConfirmed } from '../../lib/push';

// FABTEXS AI — DEFAULT notification-alert layer (Shopify-style).
// Reuses the EXISTING seller notification endpoint/poll (no new architecture,
// no backend change, no opt-in). For every genuinely-new notification event it:
//   • plays a short pleasant chime (once), and
//   • shows a browser/system notification (works when the tab is backgrounded),
// while updating nothing about the existing bell/read/unread behaviour.
// Duplicate protection: a persisted set of already-alerted notification IDs, so
// refresh / re-fetch / tab-switch / re-render never re-alerts the same event.
const SEEN_KEY = 'fabtexs_alerted_notif_ids';
const POLL_MS = 12000;
// Original cash-register "ka-ching" — the DEFAULT FABTEXS seller alert sound.
const SOUND_URL = '/sounds/kaching.wav';

const loadSeen = () => {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); }
  catch { return new Set(); }
};
const saveSeen = (set) => {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set].slice(-500))); } catch {}
};

export default function NotificationAlerts() {
  const seen = useRef(loadSeen());
  const audioCtx = useRef(null);
  const buffer = useRef(null);           // decoded ka-ching AudioBuffer
  const primed = useRef(false);          // first fetch just seeds seen-set (no burst of chimes)
  const unlocked = useRef(false);

  // Keep the seller's web-push subscription alive (silent — never prompts).
  // The poll + ka-ching below only run while this page is alive, so they cannot
  // fire once Android freezes or evicts the app. A Service Worker push is the
  // only path the OS wakes with the screen off, so this registration is what
  // makes closed / locked-phone seller delivery possible. `/api/push/
  // subscribe-seller` already existed on the backend but nothing ever called
  // it, which is why `push_subscriptions` held no seller rows at all.
  // Additive: nothing below is changed.
  useEffect(() => { syncSellerPush().catch(() => {}); }, []);

  useEffect(() => {
    // Unlock audio + request notification permission on the seller's FIRST
    // interaction with FABTEXS (respects browser autoplay policy; no opt-in UI).
    const unlock = () => {
      unlocked.current = true;
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC && !audioCtx.current) audioCtx.current = new AC();
        if (audioCtx.current && audioCtx.current.state === 'suspended') audioCtx.current.resume();
        // Decode the cash-register sound once, up front, so the very first
        // alert plays without a fetch/decode delay.
        if (audioCtx.current && !buffer.current) {
          fetch(SOUND_URL)
            .then((r) => r.arrayBuffer())
            .then((buf) => audioCtx.current.decodeAudioData(buf))
            .then((decoded) => { buffer.current = decoded; })
            .catch(() => {});
        }
      } catch {}
      if ('Notification' in window && Notification.permission === 'default') {
        // Inside a real user gesture — complete the push subscribe in the same
        // step so granting permission actually yields a subscription.
        syncSellerPush({ requestPermission: true }).catch(() => {});
      }
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  const chime = () => {
    const ctx = audioCtx.current;
    if (!ctx || !unlocked.current || !buffer.current) return;  // respects autoplay policy
    try {
      if (ctx.state === 'suspended') ctx.resume();
      const src = ctx.createBufferSource();
      src.buffer = buffer.current;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.9, ctx.currentTime);
      src.connect(gain);
      gain.connect(ctx.destination);
      src.start();                                   // plays once (no loop)
    } catch {}
  };

  // This in-app checker is the seller's ORIGINAL working notification path: it
  // runs whenever the dashboard is alive (foreground or backgrounded but not
  // yet evicted) and raises its own banner + ka-ching.
  //
  // It stands down ONLY when push is PROVEN to be delivering for this seller —
  // a real subscription, a recent accepted send, and a device-confirmed render.
  // It previously stood down as soon as a PushSubscription object existed,
  // which proves nothing: seller push had never actually delivered, so the
  // fallback switched itself off and sellers were left with NOTHING when the
  // app was closed. Defaults to false (fallback ON) and fails closed.
  const pushProven = useRef(false);

  const systemNotify = (n) => {
    try {
      if (pushProven.current) return;
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      const note = new Notification(n.title || 'FABTEXS AI', {
        body: n.body || 'You have a new notification',
        tag: n.id,                 // tag dedupes at the OS level too
        icon: '/favicon.ico',
      });
      note.onclick = () => { try { window.focus(); window.location.assign('/notifications'); } catch {} };
    } catch {}
  };

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        pushProven.current = await isPushDeliveryConfirmed({ ownerKind: 'seller' });
        const { data } = await api.get('/notifications');
        if (!alive) return;
        const items = data.items || [];
        if (!primed.current) {
          // First run after (re)load: mark everything already-present as seen so
          // a refresh / SSE reconnect never replays a burst of old alerts.
          items.forEach((n) => n.id && seen.current.add(n.id));
          saveSeen(seen.current);
          primed.current = true;
          return;
        }
        const fresh = items.filter((n) => n.id && !n.is_read && !seen.current.has(n.id));
        if (fresh.length) {
          fresh.forEach((n) => seen.current.add(n.id));
          saveSeen(seen.current);
          chime();                                   // once per batch (no loop)
          fresh.slice(0, 3).forEach(systemNotify);   // system alerts (bg tabs)
        }
      } catch {}
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  return null;
}
